/**
 * Custom hooks for follow-ups feature
 */

import { useState, useEffect, useCallback } from "react";
import * as followUpService from "../services/followUpService.js";
import * as entitySearchService from "../services/entitySearchService.js";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Hook for fetching follow-ups with filtering
 * @param {Object} filters - Filter criteria
 * @returns {Object} Follow-ups data and loading state
 */
export const useFollowUps = (filters = {}) => {
  return useQuery({
    queryKey: ["followups", filters],
    queryFn: async () => {
      const result = await followUpService.getFollowUps(filters);
      
      // Always return data structure, even on error
      if (result.success) {
        return {
          followUps: result.data || [],
          count: result.count || 0,
        };
      }
      
      // If there's an error, log it but return empty array
      // This way we show "no follow-ups" instead of error when there are none
      console.warn("Follow-ups fetch returned error:", result.error);
      
      // Only throw for actual API failures (network errors, 500, etc.)
      // For "not found" or empty results, return empty array
      if (result.error && (
        result.error.includes("not found") ||
        result.error.includes("No follow-ups") ||
        result.error.toLowerCase().includes("empty")
      )) {
        return {
          followUps: [],
          count: 0,
        };
      }
      
      // For other errors, still return empty array but log the error
      // This prevents showing error message when there are simply no follow-ups
      return {
        followUps: [],
        count: 0,
      };
    },
    staleTime: 30000, // 30 seconds
    retry: false, // Don't retry to avoid showing errors for empty results
  });
};

/**
 * Hook for fetching a single follow-up
 * @param {string} followUpId - Follow-up identifier
 * @returns {Object} Follow-up data and loading state
 */
export const useFollowUp = (followUpId) => {
  return useQuery({
    queryKey: ["followup", followUpId],
    queryFn: async () => {
      if (!followUpId) return null;
      const result = await followUpService.getFollowUpById(followUpId);
      if (result.success && result.data) {
        return result.data;
      }
      // Throw error so React Query shows error state
      throw new Error(result.error || "Failed to fetch follow-up");
    },
    enabled: !!followUpId,
    staleTime: 30000,
    retry: 1,
  });
};

/**
 * Hook for fetching today's follow-ups
 * @param {string} [assignedTo] - Filter by assigned user
 * @returns {Object} Today's follow-ups data
 */
export const useTodaysFollowUps = (assignedTo = null, queryOptions = {}) => {
  const { enabled = true } = queryOptions;
  return useQuery({
    queryKey: ["followups", "today", assignedTo],
    queryFn: async () => {
      const result = await followUpService.getTodaysFollowUps(assignedTo);
      if (result.success) {
        return result.data || result;
      }
      throw new Error(result.error || "Failed to fetch today's follow-ups");
    },
    enabled,
    staleTime: 60000, // 1 minute
    refetchInterval: enabled ? 300000 : false, // Refetch every 5 minutes
  });
};

/**
 * Hook for entity search with debouncing
 * @param {string} searchTerm - Search term
 * @param {Object} [options] - Search options
 * @returns {Object} Search results and loading state
 */
export const useEntitySearch = (searchTerm, options = {}) => {
  const [debouncedTerm, setDebouncedTerm] = useState("");

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setDebouncedTerm("");
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim());
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const shouldSearch = Boolean(debouncedTerm && debouncedTerm.trim().length >= 2);

  return useQuery({
    queryKey: ["entitySearch", debouncedTerm, options],
    queryFn: async () => {
      if (!shouldSearch) {
        return [];
      }
      try {
        console.log("🔍 [ENTITY SEARCH] Searching for:", debouncedTerm);
        const results = await entitySearchService.searchEntities(debouncedTerm, options);
        console.log("✅ [ENTITY SEARCH] Results:", results);
        return results || [];
      } catch (error) {
        console.error("❌ [ENTITY SEARCH] Error:", error);
        return []; // Return empty array on error instead of throwing
      }
    },
    enabled: shouldSearch,
    staleTime: 10000, // 10 seconds
    retry: false, // Don't retry failed searches
  });
};

/**
 * Hook for creating a follow-up
 * @returns {Object} Mutation object
 */
export const useCreateFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (followUpData) => {
      const result = await followUpService.createFollowUp(followUpData);
      if (result.success) {
        return result.data || result;
      }
      // Throw error so React Query handles it as an error
      throw new Error(result.error || "Failed to create follow-up");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followups"] });
    },
  });
};

/**
 * Hook for updating a follow-up
 * @returns {Object} Mutation object
 */
export const useUpdateFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ followUpId, updateData }) => {
      const result = await followUpService.updateFollowUp(
        followUpId,
        updateData
      );
      if (result.success) {
        return result.data || result;
      }
      // Throw error so React Query handles it as an error
      throw new Error(result.error || "Failed to update follow-up");
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["followups"] });
      queryClient.invalidateQueries({
        queryKey: ["followup", variables.followUpId],
      });
    },
  });
};

/**
 * Hook for completing a follow-up
 * @returns {Object} Mutation object
 */
export const useCompleteFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ followUpId, followUpNotes = "" }) => {
      const result = await followUpService.completeFollowUp(
        followUpId,
        followUpNotes
      );
      if (result.success) {
        return result.data || result;
      }
      throw new Error(result.error || "Failed to complete follow-up");
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["followups"] });
      queryClient.invalidateQueries({
        queryKey: ["followup", variables.followUpId],
      });
      queryClient.invalidateQueries({ queryKey: ["followups", "today"] });
    },
  });
};

/**
 * Hook for rescheduling a follow-up
 * @returns {Object} Mutation object
 */
export const useRescheduleFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ followUpId, dueDate, scheduledDate = null }) => {
      const result = await followUpService.rescheduleFollowUp(
        followUpId,
        dueDate,
        scheduledDate
      );
      if (result.success) {
        return result.data || result;
      }
      throw new Error(result.error || "Failed to reschedule follow-up");
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["followups"] });
      queryClient.invalidateQueries({
        queryKey: ["followup", variables.followUpId],
      });
      queryClient.invalidateQueries({ queryKey: ["followups", "today"] });
    },
  });
};

/**
 * Hook for deleting a follow-up
 * @returns {Object} Mutation object
 */
export const useDeleteFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (followUpId) => {
      const result = await followUpService.deleteFollowUp(followUpId);
      if (result.success) {
        return result.data || result;
      }
      throw new Error(result.error || "Failed to delete follow-up");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followups"] });
      queryClient.invalidateQueries({ queryKey: ["followups", "today"] });
    },
  });
};

